import React, { useState } from 'react';
import {
  IAvailableWorkflowActions,
  IFiguresAndTablesFile
} from '@publishing-engine/domain/models';
import { Flex } from '@chakra-ui/react';
import { FormFieldTablesInput } from '@publishing-engine/domain/types/article';
import { FieldHeader } from '../common/FieldHeader';
import TablesUpdateModal from './TablesUpdateModal';
import { EntryPoint } from 'src/utils/types';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { TablesDiffModal } from './TablesDiffModal';
import { TablesEmptyState } from './TablesEmptyState';
import TablesDisplay from './TablesDisplay';

interface Props {
  formFieldDefinition: FormFieldTablesInput;
  value: IFiguresAndTablesFile;
  previousValue?: IFiguresAndTablesFile;
  isEditable: boolean;
  onChange: (value: IFiguresAndTablesFile) => void;
  submissionContextId: string;
  availableActions: IAvailableWorkflowActions;
  entryPoint: EntryPoint;
  isUpdateRequired?: boolean;
}

export const Tables: React.FC<Props> = ({
  formFieldDefinition,
  value,
  previousValue,
  isEditable,
  submissionContextId,
  availableActions,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated } = useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);
  const hasValue = !!value?.length;

  return (
    <Flex flexDirection={'column'} gap={2} id="Tables">
      {isEditing && (
        <TablesUpdateModal
          isOpen={isEditing}
          formField={formFieldDefinition}
          onClose={() => setIsEditing(false)}
          value={value}
          onSubmit={value => {
            setIsEditing(false);
            onChange(value.fileSet?.Tables ?? []);
          }}
          submissionContextId={submissionContextId}
        />
      )}

      {isUpdated && showDiffModal ? (
        <TablesDiffModal
          config={formFieldDefinition}
          value={value}
          previousValue={previousValue}
          onClose={closeDiffModal}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={`${formFieldDefinition.params.labels.main} (${
          value?.length ?? 0
        })`}
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
      />

      {hasValue ? (
        <TablesDisplay tables={value} />
      ) : (
        <TablesEmptyState fieldLabels={formFieldDefinition.params.labels} />
      )}
    </Flex>
  );
};
